<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import CustomLink from './CustomLink.vue';
import Button from './Button.vue';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps({
  products: {
    type: Array,
    required: true,
  },
});

// Refs for animation and navigation
const bentoContainerRef = ref(null);
const showcaseRef = ref(null);
const titleRef = ref(null);
const subtitleRef = ref(null);
const buttonRef = ref(null);
const currentSet = ref(0);
const totalSets = ref(0);
const isAnimating = ref(false);

// Calculate how many product sets we can display in the carousel
const calculateTotalSets = () => {
  // Skip first 3 products, then calculate sets of 2 for carousel
  const remainingProducts = Math.max(0, props.products.length - 3);
  totalSets.value = Math.ceil(remainingProducts / 2);
};

// Get the first 3 products to display statically
const staticProducts = computed(() => {
  return props.products.slice(0, 3);
});

// Get random projects for carousel, excluding the first 3 static ones
const getRandomCarouselProducts = () => {
  const remainingProducts = props.products.slice(3); // Skip first 3
  if (remainingProducts.length <= 2) return remainingProducts;
  
  // Create a shuffled copy of remaining products
  const shuffled = [...remainingProducts].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 2); // Take first 2 from shuffled array
};

// Get random products for display in carousel
const currentCarouselProducts = computed(() => {
  return getRandomCarouselProducts();
});

// Helper to ensure we always have 2 items per carousel set
const displayCarouselProducts = computed(() => {
  const products = [...currentCarouselProducts.value];
  while (products.length < 2) {
    // Add placeholder if needed
    products.push({ 
      meta: { id: `placeholder-${products.length}`, name: 'Placeholder' },
      style: { projectColor: 'gray' },
      content: { subTitle: 'Placeholder item', hoverCta: 'Meer info' },
      isPlaceholder: true
    });
  }
  return products;
});

// Helper to ensure we always have 3 items for static display
const displayStaticProducts = computed(() => {
  const products = [...staticProducts.value];
  while (products.length < 3) {
    // Add placeholder if needed
    products.push({ 
      meta: { id: `placeholder-${products.length}`, name: 'Placeholder' },
      style: { projectColor: 'gray' },
      content: { subTitle: 'Placeholder item', hoverCta: 'Meer info' },
      isPlaceholder: true
    });
  }
  return products;
});

// Navigation functions - updated for smoother transitions that don't block UI
const navigatePrev = () => {
  if (currentSet.value > 0 && !isAnimating.value) {
    // Set animating state but allow quick successive clicks
    isAnimating.value = true;
    
    // Start animation immediately
    gsap.to(bentoContainerRef.value, { 
      opacity: 0.5,
      duration: 0.15,
      onComplete: () => {
        // Update current set
        currentSet.value--;
        
        // Immediately fade in new content
        gsap.to(bentoContainerRef.value, { 
          opacity: 1, 
          duration: 0.15,
          onComplete: () => { 
            isAnimating.value = false; 
          }
        });
      }
    });
    
    // Reset animation state quickly to allow button clicks
    setTimeout(() => {
      isAnimating.value = false;
    }, 150);
  }
};

const navigateNext = () => {
  if (currentSet.value < totalSets.value - 1 && !isAnimating.value) {
    // Set animating state but allow quick successive clicks
    isAnimating.value = true;
    
    // Start animation immediately
    gsap.to(bentoContainerRef.value, { 
      opacity: 0.5,
      duration: 0.15,
      onComplete: () => {
        // Update current set
        currentSet.value++;
        
        // Immediately fade in new content
        gsap.to(bentoContainerRef.value, { 
          opacity: 1, 
          duration: 0.15,
          onComplete: () => { 
            isAnimating.value = false; 
          }
        });
      }
    });
    
    // Reset animation state quickly to allow button clicks
    setTimeout(() => {
      isAnimating.value = false;
    }, 150);
  }
};

// Helper function to get image for a product
const getProductImage = (product) => {
  if (product.isPlaceholder) return '';
  
  // First priority: use sliderImage if available
  if (product.media?.sliderImage) {
    return product.media.sliderImage;
  }
  
  // Second priority: fallback to showcase images
  if (product.sections?.showcase && Object.keys(product.sections.showcase).length > 0) {
    const showcaseKeys = Object.keys(product.sections.showcase);
    const randomKey = showcaseKeys[Math.floor(Math.random() * showcaseKeys.length)];
    return product.sections.showcase[randomKey].image;
  }
  
  // Third priority: use mockup image
  if (product.media?.mockupImages?.mid?.src) {
    return product.media.mockupImages.mid.src;
  }
  
  // Final fallback
  return 'https://images.unsplash.com/photo-1511376777868-611b54f68947';
};

// Helper function to split text on commas
const splitTextOnComma = (text) => {
  return text ? text.split(',').map((line, i, arr) => i < arr.length - 1 ? `${line.trim()},` : line.trim()) : [];
};

onMounted(() => {
  // Calculate how many sets of products we have
  calculateTotalSets();
  
  // Animate title and subtitle with smooth fade-in from bottom - matching ServiceTabs style
  gsap.fromTo(
    titleRef.value,
    { css: { opacity: 0, transform: 'translateY(30px)' } },
    { 
      css: { opacity: 1, transform: 'translateY(0px)' },
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: showcaseRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );
  
  gsap.fromTo(
    subtitleRef.value,
    { css: { opacity: 0, transform: 'translateY(30px)' } },
    { 
      css: { opacity: 1, transform: 'translateY(0px)' },
      duration: 0.8,
      delay: 0.2,
      ease: "power2.out",
      scrollTrigger: {
        trigger: showcaseRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );

  // Animate button with matching timing
  gsap.fromTo(
    buttonRef.value,
    { css: { opacity: 0, transform: 'translateY(30px)' } },
    { 
      css: { opacity: 1, transform: 'translateY(0px)' },
      duration: 0.8,
      delay: 0.4,
      ease: "power2.out",
      scrollTrigger: {
        trigger: showcaseRef.value,
        start: "top 80%",
        toggleActions: "play none none none"
      }
    }
  );

  // Animate bento grid with smooth fade-in - matching other components
  gsap.fromTo(
    bentoContainerRef.value,
    { css: { opacity: 0, transform: 'translateY(30px)' } },
    {
      css: { opacity: 1, transform: 'translateY(0px)' },
      duration: 0.8,
      delay: 0.6,
      ease: "power2.out",
      scrollTrigger: {
        trigger: bentoContainerRef.value,
        start: "top 85%",
        toggleActions: "play none none none"
      }
    }
  );
  
  // Add touch swipe functionality for mobile
  let touchStartX = 0;
  let touchEndX = 0;
  
  const handleTouchStart = (e) => {
    touchStartX = e.changedTouches[0].screenX;
  };
  
  const handleTouchEnd = (e) => {
    touchEndX = e.changedTouches[0].screenX;
    handleSwipe();
  };
  
  const handleSwipe = () => {
    const swipeThreshold = 50;
    if (touchEndX < touchStartX - swipeThreshold) {
      navigateNext();
    }
    if (touchEndX > touchStartX + swipeThreshold) {
      navigatePrev();
    }
  };
  
  // Add touch event listeners to the container
  const container = bentoContainerRef.value;
  if (container) {
    container.addEventListener('touchstart', handleTouchStart, false);
    container.addEventListener('touchend', handleTouchEnd, false);
  }
  
  // Clean up event listeners on unmount
  onUnmounted(() => {
    if (container) {
      container.removeEventListener('touchstart', handleTouchStart, false);
      container.removeEventListener('touchend', handleTouchEnd, false);
    }
  });
});
</script>

<template>
  <div ref="showcaseRef" class="relative w-full overflow-visible bg-white dark:bg-zinc-900 transition-colors duration-300">
    <div class="wide-container flex flex-col">
      <div class="h-full w-full text-center">
        <!-- Title section -->
        <div class="flex flex-col w-full gap-8">
          <hgroup class="flex flex-col w-full">
            <h2 class="text-pretty text-5xl font-semibold text-jet sm:text-6xl lg:text-7xl tracking-tight">Slim, <span class="bg-text-mint text-transparent bg-clip-text">Schaalbaar</span> en Krachtig.</h2>
            <p class="text-balance text-xl sm:font-medium text-jet/80 max-w-6xl mx-auto mt-6">Onze projecten laten zien hoe flexibele software echte resultaten oplevert – en hoe wij jou kunnen helpen om meer te verbinden, minder te verspillen en slimmer te werken.</p>
          </hgroup>
          
          <CustomLink href="/products" class="text-azure-600 hover:text-azure-800 transition-colors duration-300 font-medium text-lg" :useInertia="true">
            Bekijk alle projecten →
          </CustomLink>
        </div>
        
        <!-- Static Bento Grid Layout - First 3 Products -->
        <div ref="bentoContainerRef" class="grid grid-cols-1 lg:grid-cols-3 gap-8 relative h-[210vh] min-h-[2400px] max-h-[2700px] grid-rows-[40fr,55fr] my-24">
            <!-- First row: 1/3 + 2/3 width blocks -->
            <div class="lg:col-span-1 h-full">
                <CustomLink :href="!displayStaticProducts[0].isPlaceholder ? `/project/${displayStaticProducts[0].meta.slug}` : '#'" :useInertia="!displayStaticProducts[0].isPlaceholder" class="bento-card group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl dark:shadow-black/30 dark:hover:shadow-black/40 transition-all duration-500" 
                :class="{ 'pointer-events-none opacity-50': displayStaticProducts[0].isPlaceholder }">
                <!-- azure gradient backdrop (always visible) -->
                <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-azure-500 via-azure-400 to-azure-600"></div>
                <!-- Background Image (layered on top) -->
                <img :src="getProductImage(displayStaticProducts[0])" :alt="displayStaticProducts[0].meta.name" class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"/>
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
                
                <!-- Static corner arrow - bottom right with proper scaling origin -->
                <div class="absolute bottom-6 right-6 z-30 origin-bottom-right">
                    <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-azure-800 transition-all duration-300 ease-in-out group-hover/card:scale-125">
                    <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-6 w-6 group-hover/card:scale-110 transition-all duration-500 ease-in-out" />
                  </div>
                </div>
                
                <!-- Logo in top left - fades out on hover -->
                <div class="bento-logo z-10 p-8">
                    <img v-if="!displayStaticProducts[0].isPlaceholder && displayStaticProducts[0].meta.logo" :src="displayStaticProducts[0].meta.logo" :alt="displayStaticProducts[0].meta.name" class="h-20 w-auto max-w-full object-contain drop-shadow-md"/>
                </div>
                <!-- Content in bottom left - fades out on hover -->
                <div class="bento-content z-10 p-8 text-white">
                    <h3 class="text-4xl font-semibold">{{ displayStaticProducts[0].meta.name }}</h3>
                    <p class="text-xl text-white/80 line-clamp-2 mt-2">{{ displayStaticProducts[0].content.subTitle }}</p>
                </div>
                <!-- Centered hover CTA - appears on hover -->
                <div class="bento-cta absolute inset-0 z-20 flex items-center justify-center">
                    <span class="text-white font-medium text-7xl text-center text-balance p-8">
                    {{ displayStaticProducts[0].content.hoverCta || 'Meer info' }}
                    </span>
                </div>
                </CustomLink>
            </div>
            
            <div class="lg:col-span-2 h-full">
                <CustomLink :href="!displayStaticProducts[2].isPlaceholder ? `/project/${displayStaticProducts[2].meta.slug}` : '#'" :useInertia="!displayStaticProducts[2].isPlaceholder" class="bento-card group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-500"
                :class="{ 'pointer-events-none opacity-50': displayStaticProducts[2].isPlaceholder }">
                <!-- azure gradient backdrop (always visible) -->
                <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-azure-500 via-azure-400 to-azure-600"></div>
                <!-- Background Image (layered on top) -->
                <img :src="getProductImage(displayStaticProducts[2])" :alt="displayStaticProducts[2].meta.name" class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"/>
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
                
                <!-- Static corner arrow - bottom right with proper scaling origin -->
                <div class="absolute bottom-6 right-6 z-30 origin-bottom-right">
                    <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-azure-800 transition-all duration-300 ease-in-out group-hover/card:scale-125">
                    <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-6 w-6 group-hover/card:scale-110 transition-all duration-500 ease-in-out" />
                  </div>
                </div>
                
                <!-- Logo in top left - fades out on hover -->
                <div class="bento-logo relative z-10 p-8">
                    <img v-if="!displayStaticProducts[2].isPlaceholder && displayStaticProducts[2].meta.logo" :src="displayStaticProducts[2].meta.logo" :alt="displayStaticProducts[2].meta.name" class="h-20 w-auto max-w-full object-contain drop-shadow-md"/>
                </div>
                <!-- Content in bottom left - fades out on hover -->
                <div class="bento-content relative z-10 p-6 text-white">
                    <h3 class="text-4xl font-semibold">{{ displayStaticProducts[2].meta.name }}</h3>
                    <p class="text-xl text-white/80 line-clamp-2 mt-2">{{ displayStaticProducts[2].content.subTitle }}</p>
                </div>
                <!-- Centered hover CTA - appears on hover -->
                <div class="bento-cta absolute inset-0 z-20 flex items-center justify-center">
                    <span class="text-white font-medium text-7xl text-center text-balance p-8">
                    {{ displayStaticProducts[2].content.hoverCta || 'Meer info' }}
                    </span>
                </div>
                </CustomLink>
            </div>
            
            <!-- Second row: full width block -->
            <div class="lg:col-span-3 h-full">
                <CustomLink :href="!displayStaticProducts[1].isPlaceholder ? `/project/${displayStaticProducts[1].meta.slug}` : '#'" :useInertia="!displayStaticProducts[1].isPlaceholder" class="bento-card group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-500"
                :class="{ 'pointer-events-none opacity-50': displayStaticProducts[1].isPlaceholder }">
                <!-- azure gradient backdrop (always visible) -->
                <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-azure-500 via-azure-400 to-azure-600"></div>
                <!-- Background Image (layered on top) -->
                <img :src="getProductImage(displayStaticProducts[1])" :alt="displayStaticProducts[1].meta.name" class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"/>
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
                
                <!-- Static corner arrow - bottom right with proper scaling origin -->
                <div class="absolute bottom-6 right-6 z-30 origin-bottom-right">
                    <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-azure-800 transition-all duration-300 ease-in-out group-hover/card:scale-125">
                    <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-6 w-6 group-hover/card:scale-110 transition-all duration-500 ease-in-out" />
                  </div>
                </div>
                
                <!-- Logo in top left - fades out on hover -->
                <div class="bento-logo relative z-10 p-8">
                    <img v-if="!displayStaticProducts[1].isPlaceholder && displayStaticProducts[1].meta.logo" :src="displayStaticProducts[1].meta.logo" :alt="displayStaticProducts[1].meta.name" class="h-20 w-auto max-w-full object-contain drop-shadow-md"/>
                </div>
                <!-- Content in bottom left - fades out on hover -->
                <div class="bento-content relative z-10 p-6 text-white">
                    <h3 class="text-2xl font-bold">{{ displayStaticProducts[1].meta.name }}</h3>
                    <p class="text-sm text-white/80 line-clamp-2 mt-2">{{ displayStaticProducts[1].content.subTitle }}</p>
                </div>
                <!-- Centered hover CTA - appears on hover -->
                <div class="bento-cta absolute inset-0 z-20 flex items-center justify-center">
                    <span class="text-white font-medium text-7xl text-center text-balance p-8">
                    {{ displayStaticProducts[1].content.hoverCta || 'Meer info' }}
                    </span>
                </div>
                </CustomLink>
            </div>

            <!-- Carousel Section - Remaining Products -->
            <div v-if="props.products.length > 3" class="lg:col-span-3 h-full">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 relative h-full">
                    <!-- Carousel Card 1 -->
                    <div class="h-full">
                    <CustomLink :href="!displayCarouselProducts[0].isPlaceholder ? `/project/${displayCarouselProducts[0].meta.slug}` : '#'" :useInertia="!displayCarouselProducts[0].isPlaceholder" class="bento-card group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl dark:shadow-black/30 dark:hover:shadow-black/40 transition-all duration-500" 
                        :class="{ 'pointer-events-none opacity-50': displayCarouselProducts[0].isPlaceholder }">
                        <!-- azure gradient backdrop (always visible) -->
                        <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-azure-500 via-azure-400 to-azure-600"></div>
                        <!-- Background Image (layered on top) -->
                        <img :src="getProductImage(displayCarouselProducts[0])" :alt="displayCarouselProducts[0].meta.name" class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"/>
                        <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
                        
                        <!-- Static corner arrow - bottom right with proper scaling origin -->
                        <div class="absolute bottom-6 right-6 z-30 origin-bottom-right">
                          <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-azure-800 transition-all duration-300 ease-in-out group-hover/card:scale-125">
                            <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-6 w-6 group-hover/card:scale-110 transition-all duration-500 ease-in-out" />
                          </div>
                        </div>
                        
                        <!-- Logo in top left - fades out on hover -->
                        <div class="bento-logo z-10 p-8">
                            <img v-if="!displayCarouselProducts[0].isPlaceholder && displayCarouselProducts[0].meta.logo" :src="displayCarouselProducts[0].meta.logo" :alt="displayCarouselProducts[0].meta.name" class="h-20 w-auto max-w-full object-contain drop-shadow-md"/>
                        </div>
                        <!-- Content in bottom left - fades out on hover -->
                        <div class="bento-content z-10 p-8 text-white">
                            <h3 class="text-4xl font-semibold">{{ displayCarouselProducts[0].meta.name }}</h3>
                            <p class="text-xl text-white/80 line-clamp-2 mt-2">{{ displayCarouselProducts[0].content.subTitle }}</p>
                        </div>
                        <!-- Centered hover CTA - appears on hover -->
                        <div class="bento-cta absolute inset-0 z-20 flex items-center justify-center">
                            <span class="text-white font-medium text-7xl text-center text-balance p-8">
                            {{ displayCarouselProducts[0].content.hoverCta || 'Meer info' }}
                            </span>
                        </div>
                    </CustomLink>
                    </div>

                    <!-- Carousel Card 2 -->
                    <div class="h-full">
                    <CustomLink :href="!displayCarouselProducts[1].isPlaceholder ? `/project/${displayCarouselProducts[1].meta.slug}` : '#'" :useInertia="!displayCarouselProducts[1].isPlaceholder" class="bento-card group/card w-full h-full rounded-xl overflow-hidden relative flex flex-col justify-between shadow-md hover:shadow-xl dark:shadow-black/30 dark:hover:shadow-black/40 transition-all duration-500" 
                        :class="{ 'pointer-events-none opacity-50': displayCarouselProducts[1].isPlaceholder }">
                        <!-- azure gradient backdrop (always visible) -->
                        <div class="absolute inset-0 w-full h-full bg-gradient-to-br from-azure-500 via-azure-400 to-azure-600"></div>
                        <!-- Background Image (layered on top) -->
                        <img :src="getProductImage(displayCarouselProducts[1])" :alt="displayCarouselProducts[1].meta.name" class="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"/>
                        <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 group-hover/card:opacity-90"></div>
                        
                        <!-- Static corner arrow - bottom right with proper scaling origin -->
                        <div class="absolute bottom-6 right-6 z-30 origin-bottom-right">
                          <div class="flex items-center justify-center h-12 w-12 bg-white rounded-full shadow-md text-azure-800 transition-all duration-300 ease-in-out group-hover/card:scale-125">
                            <font-awesome-icon :icon="['fas', 'arrow-right']" class="h-6 w-6 group-hover/card:scale-110 transition-all duration-500 ease-in-out" />
                          </div>
                        </div>
                        
                        <!-- Logo in top left - fades out on hover -->
                        <div class="bento-logo z-10 p-8">
                            <img v-if="!displayCarouselProducts[1].isPlaceholder && displayCarouselProducts[1].meta.logo" :src="displayCarouselProducts[1].meta.logo" :alt="displayCarouselProducts[1].meta.name" class="h-20 w-auto max-w-full object-contain drop-shadow-md"/>
                        </div>
                        <!-- Content in bottom left - fades out on hover -->
                        <div class="bento-content z-10 p-8 text-white">
                            <h3 class="text-4xl font-semibold">{{ displayCarouselProducts[1].meta.name }}</h3>
                            <p class="text-xl text-white/80 line-clamp-2 mt-2">{{ displayCarouselProducts[1].content.subTitle }}</p>
                        </div>
                        <!-- Centered hover CTA - appears on hover -->
                        <div class="bento-cta absolute inset-0 z-20 flex items-center justify-center">
                            <span class="text-white font-medium text-7xl text-center text-balance p-8">
                            {{ displayCarouselProducts[1].content.hoverCta || 'Meer info' }}
                            </span>
                        </div>
                    </CustomLink>
                    </div>
                </div>
                
                <!-- Navigation controls for carousel only -->
                <!-- <div class="mt-3 flex justify-between items-center gap-4">
                    <div class="flex gap-2">
                        <span v-for="i in totalSets" :key="i" :class="`block h-1 md:h-2 w-4 md:w-8 rounded-xs transition-all duration-300 cursor-pointer ${i-1 === currentSet ? 'bg-azure-200 dark:bg-azure-400 w-4 md:w-8' : 'bg-zinc-100 dark:bg-zinc-700 hover:bg-azure-100 dark:hover:bg-azure-800'}`" @click="!isAnimating && (currentSet = i-1)"></span>
                    </div>
                    <div class="flex gap-3 items-center">
                    <button @click="navigatePrev" class="h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 bg-black dark:bg-zinc-800 text-zinc-200 dark:text-zinc-300 hover:bg-zinc-800 dark:hover:bg-zinc-700 disabled:opacity-50"
                            :class="currentSet === 0 ? 'opacity-50 cursor-not-allowed' : ''" aria-label="Previous projects">
                        <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-4 w-4" />
                    </button>
                    
                    <button @click="navigateNext" class="h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 bg-black dark:bg-zinc-800 text-zinc-200 dark:text-zinc-300 hover:bg-zinc-800 dark:hover:bg-zinc-700 disabled:opacity-50"
                            :class="currentSet >= totalSets - 1 ? 'opacity-50 cursor-not-allowed' : ''" aria-label="Next projects">
                        <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-4 w-4" />
                    </button>
                    </div>
                </div> -->
            </div>
        </div>


        <!-- Button section -->
        <div class="flex flex-col gap-8 max-w-5xl mx-auto py-24">
          <!-- Top row with icon/text and button -->
          <div class="flex flex-col items-start lg:items-center gap-6 lg:gap-12">
            <!-- Left side - Icon and text -->
            <div class="flex gap-4 w-full items-center justify-between">
                <div class="flex-shrink-0 mt-1">
                    <font-awesome-icon :icon="['fas', 'rocket']" class="text-6xl text-mint-600" />
                </div>
                <div class="flex-shrink-0">
                <Button ref="buttonRef" href="/products" variant="gradient" icon="arrow-right">
                    Projecten vol slimme oplossingen
                </Button>
                </div>
            </div>
            <div class="text-left">
                <p class="text-lg leading-relaxed">
                <span class="text-black font-medium">Klaar om jouw volgende project te starten?</span>
                <span class="text-black/90 ml-1">Elk project dat we bouwen is uniek en afgestemd op jouw specifieke behoeften. Van eenvoudige tools tot complexe platforms - we helpen je om je doelen te bereiken met slimme, schaalbare oplossingen.</span>
                </p>
            </div>
            
            <!-- Right side - Button -->
          </div>
          
          <!-- Bottom row - Statistics grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-black/20">
            <!-- Statistic 1 -->
            <div class="text-left">
              <div class="text-sm text-black/90 mb-2">Gemiddeld</div>
              <div class="text-4xl lg:text-5xl font-bold text-mint-600 mb-2">50%</div>
              <div class="text-sm text-black/90">sneller ontwikkeld</div>
            </div>
            
            <!-- Statistic 2 -->
            <div class="text-left">
              <div class="text-sm text-black/90 mb-2">Tot wel</div>
              <div class="text-4xl lg:text-5xl font-bold text-mint-600 mb-2">90%</div>
              <div class="text-sm text-black/90">minder handmatig werk</div>
            </div>
            
            <!-- Statistic 3 -->
            <div class="text-left">
              <div class="text-sm text-black/90 mb-2">Meer dan</div>
              <div class="text-4xl lg:text-5xl font-bold text-mint-600 mb-2">100+</div>
              <div class="text-sm text-black/90">tevreden klanten</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Bento card hover animations - component-specific to avoid conflicts */
.bento-card .bento-logo {
  opacity: 1;
  transition: opacity 0.3s ease;
}

.bento-card:hover .bento-logo {
  opacity: 0;
}

.bento-card .bento-content {
  opacity: 1;
  transition: opacity 0.3s ease;
}

.bento-card:hover .bento-content {
  opacity: 0;
}

.bento-card .bento-cta {
  opacity: 0;
  transition: opacity 0.5s ease;
}

.bento-card:hover .bento-cta {
  opacity: 1;
}

/* Transform origin for bottom-right scaling */
.origin-bottom-right {
  transform-origin: bottom right;
}

/* Some effects can't be directly created with Tailwind classes and need to remain as CSS */
@layer utilities {
  /* Card 3D transform handling - can be added to Tailwind config if used often */
  .transform-style-3d { transform-style: preserve-3d; }
  .backface-hidden { backface-visibility: hidden; }
  .will-change-transform { will-change: transform; }
  
  /* Apply these utility classes to the card in the template */
}
</style>
